import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { exportAttendancePDF } from "../../components/attendance/exportAttendancePDF";

export default function MyAttendance() {
  const navigate = useNavigate();
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [labour, setLabour]       = useState(null);
  const [payments, setPayments]   = useState([]);
  const [monthFilter, setMonthFilter] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { label:"Dashboard",     emoji:"🏠", path:"/labour/dashboard" },
    { label:"My Attendance", emoji:"📅", path:"/labour/attendance", active:true },
    { label:"My Salary",     emoji:"💰", path:"/labour/salary" },
    { label:"My Payments",   emoji:"💳", path:"/labour/payments" },
    { label:"Profile",       emoji:"👤", path:"/labour/profile" },
  ];

  const ST = {
    full:    { label:"Full Day",  bg:"#D1FAE5", tc:"#059669" },
    half:    { label:"Half Day",  bg:"#FEF3C7", tc:"#D97706" },
    overtime:{ label:"Overtime",  bg:"#DBEAFE", tc:"#0369A1" },
    absent:  { label:"Absent",    bg:"#FEF2F2", tc:"#DC2626" },
    holiday: { label:"Holiday",   bg:"#FEF3C7", tc:"#B45309" },
    leave:   { label:"On Leave",  bg:"#EDE9FE", tc:"#7C3AED" },
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [profileRes, attendRes] = await Promise.all([
          api.get("/labour/me"),
          api.get("/attendance/my"),
        ]);
        setLabour(profileRes.data.labour);
        // Backend returns { success, data: [...] }
        setRecords(attendRes.data?.data || attendRes.data || []);

        // try payments
        try {
          // Try /payment/my first (labour route), fallback to /:id
          const payRes = await api.get("/payment/my");
          setPayments(payRes.data?.data || payRes.data || []);
        } catch(_){
          try {
            const payRes2 = await api.get(`/payment/${profileRes.data.labour._id}`);
            setPayments(payRes2.data?.payments || payRes2.data || []);
          } catch(__) {}
        }
      } catch(e){ console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  // All months from records
  const months = [...new Set(records.map(r => r.date?.slice(0,7)))].sort().reverse();

  const filtered = monthFilter
    ? records.filter(r => r.date?.startsWith(monthFilter))
    : records;

  const sorted = [...filtered].sort((a,b) => new Date(b.date)-new Date(a.date));

  const WORKED = ["full","half","overtime"];
  const wrkD   = filtered.filter(r=>WORKED.includes(r.status)).length;
  const absD   = filtered.filter(r=>r.status==="absent").length;
  const pct    = filtered.length ? Math.round((wrkD/filtered.length)*100) : 0;

  const handlePDF = () => {
    if (!labour) return;
    exportAttendancePDF(labour.name, labour.email, labour.dailyRate, records, monthFilter, payments);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'Plus Jakarta Sans',sans-serif;background:#FAF7F4;}
        .sidebar{width:220px;flex-shrink:0;background:#fff;border-right:1.5px solid #E7E5E4;padding:24px 14px;display:flex;flex-direction:column;gap:6px;position:sticky;top:0;height:100vh;overflow-y:auto;}
        .topbar{display:none;}
        .hamburger{display:none;align-items:center;justify-content:center;width:40px;height:40px;border-radius:10px;border:1.5px solid #E7E5E4;background:#fff;cursor:pointer;flex-shrink:0;}
        .sidebar-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:40;}
        .nav-btn{display:flex;align-items:center;gap:9px;width:100%;padding:10px 12px;border-radius:10px;border:none;background:none;font-family:'Plus Jakarta Sans',sans-serif;font-size:14px;font-weight:600;color:#78716C;cursor:pointer;transition:background .15s,color .15s;text-align:left;}
        .nav-btn:hover,.nav-btn.active{background:#FEF3C7;color:#D97706;}
        .nav-btn.danger:hover{background:#FEF2F2;color:#DC2626;}
        .att-row{transition:background .15s;}
        .att-row:hover{background:#FFFBF7;}
        @media(max-width:768px){
          .sidebar{position:fixed;left:0;top:0;z-index:50;transform:translateX(-100%);transition:transform .28s ease;height:100%;}
          .sidebar.open{transform:translateX(0);}
          .sidebar-overlay.open{display:block;}
          .topbar{display:flex;align-items:center;gap:12px;padding:14px 16px;background:#fff;border-bottom:1.5px solid #E7E5E4;position:sticky;top:0;z-index:30;}
          .hamburger{display:flex;}
          .main-wrap{flex-direction:column!important;}
          .main-content{padding:16px!important;}
          .desktop-table{display:none!important;}
          .mobile-cards{display:block!important;}
        }
        .mobile-cards{display:none;}
        @media(min-width:769px) and (max-width:1100px){.sidebar{width:190px;}}
      `}</style>

      <div className="main-wrap" style={{display:"flex",minHeight:"100vh",fontFamily:"'Plus Jakarta Sans',sans-serif",background:"#FAF7F4"}}>
        <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={()=>setSidebarOpen(false)}/>

        <aside className={`sidebar ${sidebarOpen?"open":""}`}>
          <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"28px",padding:"0 4px"}}>
            <div style={{width:"36px",height:"36px",background:"linear-gradient(135deg,#92400E,#D97706)",borderRadius:"10px",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{fontSize:"18px"}}>🪚</span>
            </div>
            <div>
              <p style={{fontWeight:"800",fontSize:"15px",color:"#1C1917"}}>Craftigo</p>
              <p style={{fontSize:"11px",color:"#A8A29E",fontWeight:"500"}}>Labour Panel</p>
            </div>
          </div>
          <p style={{fontSize:"11px",fontWeight:"700",color:"#C4B5A4",letterSpacing:"0.7px",textTransform:"uppercase",padding:"0 6px",marginBottom:"6px"}}>Menu</p>
          {navItems.map(n=>(
            <button key={n.label} className={`nav-btn ${n.active?"active":""}`} onClick={()=>{navigate(n.path);setSidebarOpen(false);}}>
              {n.emoji} {n.label}
            </button>
          ))}
          <div style={{flex:1}}/>
          <div style={{padding:"12px",background:"#E0F2FE",borderRadius:"12px",border:"1px solid #BAE6FD",marginBottom:"10px"}}>
            <p style={{fontSize:"12px",fontWeight:"700",color:"#0369A1"}}>🔨 Labour</p>
            <p style={{fontSize:"11px",color:"#0369A1",marginTop:"2px",opacity:.8}}>{labour?.name||"..."}</p>
          </div>
          <button className="nav-btn danger" onClick={()=>{localStorage.clear();navigate("/login");}}>🚪 Logout</button>
        </aside>

        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
          <div className="topbar">
            <button className="hamburger" onClick={()=>setSidebarOpen(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6" x2="21" y2="6" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="12" x2="21" y2="12" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="18" x2="21" y2="18" stroke="#44403C" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <span style={{fontWeight:"800",fontSize:"16px",color:"#1C1917"}}>📅 My Attendance</span>
          </div>

          <main className="main-content" style={{padding:"28px 28px",flex:1,overflowY:"auto"}}>

            {/* Header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"20px",flexWrap:"wrap",gap:"12px"}}>
              <div>
                <h1 style={{fontSize:"24px",fontWeight:"800",color:"#1C1917",letterSpacing:"-0.5px"}}>My Attendance</h1>
                <p style={{fontSize:"13px",color:"#A8A29E",marginTop:"3px"}}>{sorted.length} records</p>
              </div>
              <button onClick={handlePDF}
                style={{padding:"10px 18px",background:"linear-gradient(135deg,#92400E,#D97706)",border:"none",borderRadius:"12px",fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"13px",fontWeight:"700",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",gap:"6px",boxShadow:"0 4px 14px rgba(180,83,9,.3)"}}>
                📄 Download PDF
              </button>
            </div>

            {/* Stats row */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:"10px",marginBottom:"20px"}}>
              {[
                {label:"Worked",     val:wrkD, bg:"#D1FAE5",border:"#A7F3D0",tc:"#059669"},
                {label:"Absent",     val:absD, bg:"#FEF2F2",border:"#FECACA",tc:"#DC2626"},
                {label:"Attendance", val:pct+"%", bg:"#FEF3C7",border:"#FDE68A",tc:"#D97706"},
              ].map(s=>(
                <div key={s.label} style={{padding:"14px",borderRadius:"14px",background:s.bg,border:`1.5px solid ${s.border}`,textAlign:"center"}}>
                  <p style={{fontSize:"20px",fontWeight:"800",color:s.tc}}>{s.val}</p>
                  <p style={{fontSize:"12px",fontWeight:"600",color:s.tc,opacity:.8}}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Month filter */}
            <div style={{background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"14px",padding:"14px 16px",marginBottom:"16px",display:"flex",alignItems:"center",gap:"12px",flexWrap:"wrap"}}>
              <p style={{fontSize:"13px",fontWeight:"700",color:"#44403C"}}>Filter by Month:</p>
              <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
                style={{padding:"8px 14px",border:"1.5px solid #E7E5E4",borderRadius:"10px",fontFamily:"'Plus Jakarta Sans',sans-serif",fontSize:"13px",color:"#1C1917",background:"#FFF7ED",outline:"none",cursor:"pointer"}}>
                <option value="">All Time</option>
                {months.map(m=>(
                  <option key={m} value={m}>
                    {new Date(m+"-01").toLocaleDateString("en-IN",{month:"long",year:"numeric"})}
                  </option>
                ))}
              </select>
              {monthFilter && (
                <button onClick={()=>setMonthFilter("")} style={{padding:"8px 12px",borderRadius:"10px",border:"1.5px solid #E7E5E4",background:"#fff",fontSize:"12px",fontWeight:"700",color:"#78716C",cursor:"pointer"}}>
                  Clear ×
                </button>
              )}
            </div>

            {/* DESKTOP TABLE */}
            <div className="desktop-table" style={{background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"16px",overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr 1fr 1.5fr",padding:"12px 20px",background:"#FAF7F4",borderBottom:"1.5px solid #E7E5E4"}}>
                {["Date","Day","Status","OT Hours","Marked By"].map(h=>(
                  <span key={h} style={{fontSize:"11px",fontWeight:"700",color:"#A8A29E",letterSpacing:"0.6px",textTransform:"uppercase"}}>{h}</span>
                ))}
              </div>
              {loading ? (
                <div style={{padding:"48px",textAlign:"center"}}>
                  <div style={{width:"32px",height:"32px",border:"3px solid #FDE68A",borderTop:"3px solid #D97706",borderRadius:"50%",animation:"spin .7s linear infinite",margin:"0 auto"}}/>
                </div>
              ) : sorted.length===0 ? (
                <div style={{padding:"48px",textAlign:"center"}}>
                  <p style={{fontSize:"32px",marginBottom:"10px"}}>📅</p>
                  <p style={{color:"#78716C",fontWeight:"600"}}>No attendance records</p>
                </div>
              ) : sorted.map((r,i)=>{
                const d  = new Date(r.date);
                const st = ST[r.status]||{label:r.status,bg:"#F5F5F4",tc:"#78716C"};
                return (
                  <div key={r._id||i} className="att-row"
                    style={{display:"grid",gridTemplateColumns:"2fr 2fr 1fr 1fr 1.5fr",padding:"13px 20px",borderBottom:i<sorted.length-1?"1px solid #F5F5F4":"none",alignItems:"center"}}>
                    <p style={{fontSize:"14px",fontWeight:"600",color:"#1C1917"}}>{d.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</p>
                    <p style={{fontSize:"13px",color:"#78716C"}}>{d.toLocaleDateString("en-IN",{weekday:"long"})}</p>
                    <span style={{display:"inline-flex",padding:"3px 10px",borderRadius:"20px",fontSize:"11px",fontWeight:"700",background:st.bg,color:st.tc,width:"fit-content"}}>{st.label}</span>
                    <p style={{fontSize:"13px",color:"#78716C"}}>{r.overtimeHours?`${r.overtimeHours}h`:"—"}</p>
                    <p style={{fontSize:"13px",color:"#78716C"}}>{r.carpenter?.name||"Carpenter"}</p>
                  </div>
                );
              })}
            </div>

            {/* MOBILE CARDS */}
            <div className="mobile-cards">
              {loading ? (
                <div style={{padding:"48px",textAlign:"center"}}>
                  <div style={{width:"32px",height:"32px",border:"3px solid #FDE68A",borderTop:"3px solid #D97706",borderRadius:"50%",animation:"spin .7s linear infinite",margin:"0 auto"}}/>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  {sorted.map((r,i)=>{
                    const d  = new Date(r.date);
                    const st = ST[r.status]||{label:r.status,bg:"#F5F5F4",tc:"#78716C"};
                    return (
                      <div key={r._id||i} style={{background:"#fff",border:"1.5px solid #E7E5E4",borderRadius:"14px",padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px"}}>
                        <div>
                          <p style={{fontSize:"14px",fontWeight:"700",color:"#1C1917"}}>{d.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</p>
                          <p style={{fontSize:"12px",color:"#A8A29E"}}>{d.toLocaleDateString("en-IN",{weekday:"long"})}</p>
                          {r.overtimeHours?<p style={{fontSize:"12px",color:"#0369A1",fontWeight:"600",marginTop:"2px"}}>⏱ {r.overtimeHours}h OT</p>:null}
                        </div>
                        <span style={{padding:"5px 12px",borderRadius:"20px",fontSize:"12px",fontWeight:"700",background:st.bg,color:st.tc,flexShrink:0}}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}